import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  FormGroup,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
} from "@mui/material";
import { toast } from "react-hot-toast";
import { isSubscriptionBlockedError, checkSubscriptionActive } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import {
  addRole,
  clearSuccessMessage,
} from "../../../redux/Slice/Settings/addfunctionsSlice";
import { fetchRolesWithUsage } from "../../../redux/Slice/Settings/settingsSlice";

// Calls' Add/Delete keys mean Inbound (answer/reject) / Outbound (dial) —
// same underlying schema fields as every other View/Add/Edit/Delete group,
// just relabeled for this one group so the checkboxes read correctly.
const PERM_KEY_LABELS = { Calls: { Add: "Inbound", Delete: "Outbound" } };
const permKeyLabel = (group, key) => PERM_KEY_LABELS[group]?.[key] || key;

/** Must match permKeys used when rendering each group. */
const getAddRoleVisiblePermKeys = (group, permissions) => {
  const groupPermissions = permissions[group] || {};
  if (group === "Security") return ["View", "Edit"];
  if (group === "TaskTemplates") return ["View", "Create", "Edit"];
  return Object.keys(groupPermissions);
};

const areAllAddRolePermissionsSelected = (permissions, showGroups = true) => {
  // A hidden Groups switch (account without QR) must not count for, or be changed by, "Select All".
  const groups = Object.keys(permissions || {}).filter((g) => showGroups || g !== "Groups");
  if (groups.length === 0) return false;
  return groups.every((group) => {
    const keys = getAddRoleVisiblePermKeys(group, permissions);
    const gp = permissions[group] || {};
    return keys.length > 0 && keys.every((k) => Boolean(gp[k]));
  });
};

const AddRoles = ({ onBack }) => {
  // Groups (WhatsApp QR) is configurable only for accounts staff enabled QR for -- the same flag the Inbox/Integrations use.
  const showGroupsPermission = useSelector((s) => Boolean(s.workspace?.currentWorkspace?.qrPilotEnabled));
  const dispatch = useDispatch();
  const { loading, successMessage, error } = useSelector(
    (state) => state.addFunctions
  );

  const [formData, setFormData] = useState({
    roleName: "",
    description: "",
    permissions: {
      Conversation: {
        View: false,
        ViewAll: false,
        EditAll: false,
        ViewAssignedToTeam: false,
        EditAssignedToTeam: false,
        ViewAssignedToTeamMembers: false,
        EditAssignedToTeamMembers: false,
        ViewAssignedOnly: false,
        EditAssignedOnly: false,
        ViewUnassigned: false,
      },
      Contact: {
        View: false,
        Add: false,
        ViewPersonalIdentificationInfo: false,
        Edit: false,
        Delete: false,
        Export: false,
        Import: false,
      },
      Role: { View: false, Add: false, Edit: false, Delete: false },
      Team: { View: false, Add: false, Edit: false, Delete: false },
      TeamMember: { View: false, Add: false, Edit: false, Delete: false },
      Dashboard: { View: false },
      Label: { View: false, Add: false, Edit: false, Delete: false },
      Notes: { View: false, Add: false, Edit: false, Delete: false },
      Workspace: { View: false, Edit: false, Delete: false },
      Profile: { Edit: true },
      Reports: { View: false },
      Templates: { View: false, Add: false },
      TaskTemplates: { View: false, Create: false, Edit: false },
      Tasks: { ViewAll: false, ViewAssignedOnly: false, Create: false },
      Workflow: { View: false },
      AI: { View: false, Edit: false },
      Security: { View: false, Edit: false },
      Calls: { View: false, Add: false, Edit: false, Delete: false },
      QuickReplies: { View: false, Add: false, Edit: false, Delete: false },
      ContactProfileFields: { View: false, Add: false, Edit: false, Delete: false },
      // WhatsApp (QR) groups: the Groups tab and group chats in the Inbox. Shown in the form only for accounts that use QR.
      Groups: { View: false },
      // Account-wide notification sound (Settings -> Notifications). Owner
      // always bypasses this in roleauth regardless of this checkbox.
      Notifications: { Edit: false },
      // Settings -> Integrations page: View shows the tab, Manage allows the connect/disconnect buttons on the cards.
      Integrations: { View: false, Manage: false },
    },
  });

  const handleSelectAllPermissions = (shouldSelectAll) => {
    setFormData((prevData) => {
      const updatedPermissions = {};

      Object.keys(prevData.permissions).forEach((group) => {
        const groupPermissions = prevData.permissions[group] || {};
        if (group === "Groups" && !showGroupsPermission) {
          updatedPermissions[group] = groupPermissions; // hidden: never changed by Select All
          return;
        }
        updatedPermissions[group] = Object.keys(groupPermissions).reduce(
          (acc, key) => {
            acc[key] = shouldSelectAll;
            return acc;
          },
          {}
        );
      });

      return {
        ...prevData,
        permissions: updatedPermissions,
      };
    });
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleToggleChange = (group, key) => {
    setFormData((prevData) => {
      const newValue = !prevData.permissions[group][key];
      const updatedPermissions = {
        ...prevData.permissions,
        [group]: {
          ...prevData.permissions[group],
          [key]: newValue,
        },
      };

      if (group === "Security" && key === "Edit" && newValue) {
        updatedPermissions[group].View = true;
      }
      if (group === "AI" && key === "Edit" && newValue) {
        updatedPermissions[group].View = true;
      }

      // If View is disabled, turn off other perms in that group (TaskTemplates exempt — Create/Edit stand alone)
      if (key === "View" && !newValue && group !== "TaskTemplates") {
        const groupPermissions = prevData.permissions[group] || {};
        Object.keys(groupPermissions).forEach((permKey) => {
          if (permKey !== "View") {
            updatedPermissions[group][permKey] = false;
          }
        });
      }

      return {
        ...prevData,
        permissions: updatedPermissions,
      };
    });
  };

  const buildRolePayload = (data) => {
    const permissions = { ...data.permissions };
    if (permissions.Security) {
      const s = permissions.Security;
      permissions.Security = {
        View: !!(s.View || s.Edit || s.Update || s.TwoFactor || s.AutoPasswordChange),
        Edit: !!(s.Edit || s.Update || s.AutoPasswordChange),
      };
    }
    return {
      roleName: data.roleName,
      description: data.description,
      permissions,
    };
  };

  const handleSubmit = async () => {
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
    dispatch(addRole(buildRolePayload(formData)));
  };

  // Handle success or error messages
  useEffect(() => {
    if (successMessage) {
      dispatch(fetchRolesWithUsage());
      dispatch(clearSuccessMessage());
      toast.success(successMessage);
      onBack();
    }
    if (error) {
      if (!isSubscriptionBlockedError(error)) {
        setSnackbar({ open: true, message: error, severity: "error" });
      }
      dispatch(clearSuccessMessage()); // Clear error message after handling
    }
  }, [successMessage, error, dispatch, onBack]);

  return (
    <Box
      sx={{
        padding: "2rem",
        paddingBottom: {
          xs: "calc(2rem + var(--mobile-bottom-nav-height, 56px) + env(safe-area-inset-bottom, 0px))",
          sm: "calc(2rem + var(--mobile-bottom-nav-height, 62px) + env(safe-area-inset-bottom, 0px))",
          md: "2rem",
          lg: "2rem",
          xl: "2rem",
        },
        backgroundColor: "background.paper",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        height: "100%",
        minHeight: 0,
        maxHeight: "100%",
        boxSizing: "border-box",
        flex: 1,
        overflowY: "auto",
        color: "text.primary",
        // Decrease scrollbar size for all screen sizes
        scrollbarWidth: { xs: "thin", sm: "thin", md: "thin", lg: "thin", xl: "thin" }, // Firefox
        "&::-webkit-scrollbar": {
          width: { xs: "4px", sm: "4px", md: "3px", lg: "3px", xl: "3px" }, // Very thin scrollbar for md/lg/xl
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: (theme) => theme.palette.mode === "dark" ? "#555" : "#888",
          borderRadius: "10px",
          "&:hover": {
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#777" : "#555",
          },
        },
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{
          marginBottom: "1rem",
          textTransform: "capitalize",
          fontWeight: "bold",
          color: "primary.main",
          "&:hover": {
            backgroundColor: "rgba(14, 144, 70, 0.08)",
          },
        }}
      >
        Back
      </Button>

      <Typography
        variant="h5"
        sx={{ fontWeight: "bold", marginBottom: "1rem" }}
      >
        Details
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{ marginBottom: "2rem", color: "text.secondary" }}
      >
        Enter details about the role and description
      </Typography>

      <Box component="form" noValidate autoComplete="off">
        <TextField
          label="Role"
          name="roleName"
          fullWidth
          value={formData.roleName}
          onChange={handleInputChange}
          sx={(theme) => ({
            marginBottom: "1.5rem",
            "& .MuiInputLabel-root": {
              color: "text.secondary",
            },
            "& .MuiInputBase-input": {
              color: "text.primary",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
              },
              "&:hover fieldset": {
                borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.87)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "primary.main",
              },
            },
          })}
        />
        <TextField
          label="Description"
          name="description"
          fullWidth
          multiline
          rows={3}
          value={formData.description}
          onChange={handleInputChange}
          sx={(theme) => ({
            marginBottom: "2rem",
            "& .MuiInputLabel-root": {
              color: "text.secondary",
            },
            "& .MuiInputBase-input": {
              color: "text.primary",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
              },
              "&:hover fieldset": {
                borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.87)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "primary.main",
              },
            },
          })}
        />

        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", marginBottom: "1rem" }}
        >
          Permissions
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ marginBottom: "1.5rem", color: "text.secondary" }}
        >
          These permissions will be applied to each member with this role
        </Typography>

        {/* Global Select All for all permissions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          {(() => {
            const allSelected = areAllAddRolePermissionsSelected(
              formData.permissions,
              showGroupsPermission
            );

            return (
              <FormControlLabel
                control={
                  <Switch
                    checked={allSelected}
                    onChange={() =>
                      handleSelectAllPermissions(!allSelected)
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "primary.main",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "primary.main",
                        },
                    }}
                  />
                }
                label="Select All Permissions"
                sx={{
                  ".MuiFormControlLabel-label": {
                    fontSize: "0.9rem",
                    fontWeight: 500,
                  },
                }}
              />
            );
          })()}
        </Box>

        {Object.keys(formData.permissions).map((group) => {
          if (group === "Groups" && !showGroupsPermission) return null;
          const groupPermissions = formData.permissions[group] || {};
          const permKeys =
            group === "Security"
              ? ["View", "Edit"]
              : group === "TaskTemplates"
              ? ["View", "Create", "Edit"]
              : Object.keys(groupPermissions);

          return (
            <Box key={group} sx={{ marginBottom: "1rem" }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", marginBottom: "0.5rem" }}
              >
                {group}
              </Typography>
              <FormGroup row>
                {permKeys.map((key) => {
                  // Other groups: depend on View. TaskTemplates: Create/Edit independent of View.
                  const viewPermission = groupPermissions["View"];
                  const isViewDisabled = viewPermission === false;
                  const viewRequired = group !== "TaskTemplates";
                  const isDisabled = viewRequired && isViewDisabled && key !== "View";

                  return (
                    <FormControlLabel
                      key={key}
                      control={
                        <Switch
                          checked={groupPermissions[key]}
                          onChange={() => handleToggleChange(group, key)}
                          disabled={isDisabled}
                          sx={(theme) => ({
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "primary.main",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "primary.main",
                              },
                            "& .Mui-disabled": {
                              color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.38)",
                            },
                          })}
                        />
                      }
                      label={permKeyLabel(group, key)}
                      sx={{
                        color: "text.primary",
                        marginRight: "1rem",
                      }}
                    />
                  );
                })}
              </FormGroup>
            </Box>
          );
        })}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{
            marginTop: "2rem",
            textTransform: "capitalize",
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
          disabled={loading}
        >
          {loading ? "Creating..." : "Add Role"}
        </Button>
      </Box>

      {/* Snackbar for success and error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddRoles;
